<template>
  <div>
    <!-- Table Controls & Mobile Sort -->
    <table-controls
        v-if="showControls"
        :filter="filter"
        :filters="filters"
        :order="isAsc ? 'asc' : 'desc'"
        :perPage="myPerPage"
        :show-filter="showFilter"
        :show-per-page="showPerPage"
        :showSearch="showSearch"
        :show-sort="showSort"
        :sort="myDefaultSort"
        :sort-columns="sortColumns"
        @filter="onFilter"
        @per-page="onPerPage"
        @search="onSearch"
        @sort="onSort"></table-controls>
    <div>
      <!-- Non-table tables -->
      <div v-if="nonTable" class="w-full">
        <div v-if="visibleData.length" :class="tableWrapClass">
          <template v-for="(row,index) in visibleData">
            <slot :row="row" :index="index"></slot>
          </template>
        </div>
        <div v-else-if="initialized">
          <slot :filtered="!!filter || !!search" name="empty">missing 'empty' slot</slot>
        </div>
      </div>

      <!-- Standard Table -->
      <div v-else :class="tableWrapClass">
        <table>
          <!-- Table Header -->
          <thead class="table-head">
          <tr>
            <th v-if="!column.hasOwnProperty('visible') || column.visible"
                v-for="(column) in myColumns"
                @click.stop="sort(column)" :class="getTableHeadersClass(column)">
              <span>{{ column.label }}</span>
              <div v-if="column.field && column.field === currentSortColumn.field" class="inline">
                <sort-down-icon v-if="isAsc" class="-mt-px inline w-3 h-3"></sort-down-icon>
                <sort-up-icon v-else class="-mt-px inline w-3 h-3"></sort-up-icon>
              </div>
              <template v-if="column.tooltip">
                <div class="tooltip cursor-pointer ml-3 normal-case break-normal whitespace-normal" @click.stop.prevent>
                  <info-icon width="12"></info-icon>
                  <span class="tooltiptext -ml-48 mt-2" v-html="column.tooltip"></span>
                </div>
              </template>
            </th>
          </tr>
          </thead>
          <!-- Body With Data -->
          <tbody v-if="visibleData.length">
          <tr v-for="(row,index) in visibleData" @click="rowClicked(row, $event)" :class="{clickable:rowClick}">
            <slot :row="row" :index="index">table missing default slot</slot>
          </tr>
          </tbody>
          <!-- No Data -->
          <tbody v-else-if="initialized">
          <tr>
            <td :colspan="myColumns.length">
              <slot :filtered="!!filter || !!search" name="empty"></slot>
            </td>
          </tr>
          </tbody>
          <!-- if we need to have a footer on the table, use a named slot and have it here -->
        </table>
      </div>
    </div>
    <pagination v-if="showPagination && visibleData.length" class="mt-5" :current="myCurrentPage" :per-page="myPerPage"
                :total="myTotal" @set-page="onSetPage">
      <template slot="position">{{ position }}</template>
    </pagination>
  </div>
</template>
<style module>
.force-overflow {
  overflow: auto !important;
}
</style>
<script>
import InfoIcon from "../Icons/InfoIcon";

export default {
  name: "my-table",
  components: {InfoIcon},
  props: {
    // path to api call
    api: {type: String, default: ''},
    // pass extra options to the api call (headers, extra querystring values, etc)
    apiOptions: {
      type: Object, default: () => {
      }
    },

    // columns if not using <table-column>
    columns: {type: Array, default: () => []},

    // // collapseSize: {type: String, default: 'md'},

    // default current page
    currentPage: {type: Number, default: 1},

    // passed in data if not using api
    data: {type: Array, default: () => []},

    // default sort column and direction
    defaultSort: {type: String, default: ''},
    defaultSortDirection: {type: String, default: 'asc'},

    // method to filter non-api driven tables
    filterMethod: {type: Function},

    // initial filter value in select list of filters
    filterStart: {type: String, default: ''},

    // select list of filters
    filters: {type: Array, default: () => []},

    // for cookies saving/restoring
    id: {type: String, default: 'default'},

    // noHeader: {type: Boolean, default: false},

    // For my-table build on by divs instead of table html
    nonTable: {type: Boolean, default: false},

    // default number of rows per page
    perPage: {type: Number, default: 20},

    // emit @rowClicked if row is clicked on?
    rowClick: {type: Boolean, default: false},

    // option method to filter searches on non-api driven tables
    searchMethod: {type: Function},

    // show various controls?
    showFilter: {type: Boolean, default: false},
    showPagination: {type: Boolean, default: false},
    showPerPage: {type: Boolean, default: false},
    showSearch: {type: Boolean, default: false},
    showSort: {type: Boolean, default: false},

    // initial sort columns if not using <table-column>
    sorts: {type: Array, default: () => []},

    // table classes
    tableClass: {type: String, default: 'table-mobile sm:table-normal'},

    // // tableWrapClass: {type: String, default: 'w-full'},
    stickyHeaders: {type: Boolean, required: false, default: false},

    units: {type: String, default: ''}
  },
  data() {
    let cookie = this.tableCookieRead(this.id);
    let order = cookie.order ? cookie.order : this.defaultSortDirection;
    return {

      // used by <table-column) to assign sort columns
      _isTable: true,

      // contains
      currentSortColumn: {},

      // has table been initialized
      initialized: false,

      // is ascending?
      isAsc: order === 'asc',

      // current filter in select list of filter
      filter: this.filterStart,

      // tableParams for last api call
      lastFetch: {},

      // so checkSort() knows when to initialize
      firstTimeSort: true,

      // current page
      myCurrentPage: this.currentPage,

      // current columns in table
      myColumns: [...this.columns],

      // current data in table
      myData: [...this.data],

      // default sort column & direction
      myDefaultSort: cookie.sort ? cookie.sort : this.defaultSort,
      myDefaultSortDirection: cookie.order ? cookie.order : this.defaultSortDirection,

      // current per page setting
      myPerPage: cookie.perPage ? cookie.perPage : this.perPage,

      // current total # of items
      myTotal: this.data.length,

      // current search string
      search: '',

      // column names that can be sorted
      sortColumns: [...this.sorts],

      // total # of items
      unfilteredCount: this.data.length
    }
  },
  computed: {
    position() {
      if (!this.units || this.unfilteredCount < 1) {
        return null;
      }
      if (this.myTotal === this.unfilteredCount) {
        return 'Showing ' + this.numberFormat(this.visibleStart) +
            ' to ' + this.numberFormat(this.visibleEnd) + ' (of ' +
            this.numberFormat(this.myTotal) + ' ' + this.units + ')';
      }
      return 'Showing ' + this.numberFormat(this.visibleStart) + ' to ' +
          this.numberFormat(this.visibleEnd) + ' (' +
          this.numberFormat(this.myTotal) + ' filtered from ' +
          this.numberFormat(this.unfilteredCount) + ' ' + this.units + ')';
    },
    // We show the table controls if the table is not empty and one of the components wants us to
    showControls() {
      return this.unfilteredCount !== 0 && (this.showFilter || this.showPerPage || this.showSearch);
    },
    tableWrapClass() {
      let classes = "table-wrap";
      if (this.showControls) {
        classes += " mt-6";
      }
      if (this.rowClick) {
        classes += " clickable-rows";
      }
      if (this.stickyHeaders) {
        classes += " force-overflow";
      }
      return classes + ' ' + this.tableClass;
    },
    visibleData() {
      if (!this.showPagination) {
        return this.myData;
      }
      if (this.myData.length <= this.myPerPage) {
        return this.myData
      } else {
        let start = (this.myCurrentPage - 1) * this.myPerPage;
        let end = start + this.myPerPage;
        return this.myData.slice(start, end);
      }
    },
    visibleEnd() {
      let end = this.myPerPage * this.myCurrentPage;
      return end < this.myTotal ? end : this.myTotal;
    },
    visibleStart() {
      return this.myPerPage * (this.myCurrentPage - 1) + 1;
    }
  },
  watch: {
    myColumns(value) {
      let sortColumns = [];
      this.myColumns.forEach((col) => {
        if (col.sortable) {
          sortColumns.push({
            label: "Order by " + col.label,
            value: col.field
          });
        }
      });
      this.sortColumns = sortColumns;
      this.checkSort()
    },
  },
  mounted() {
    if (this.api) {
      this.$nextTick(() => {
        this.callApi()
      });
    } else {
      this.checkSort();
      this.initialized = true;
    }
  },
  methods: {
    getTableHeadersClass(column) {
      let thClass = 'whitespace-wrap hover:z-50 ';
      thClass += (this.isColumnSortable(column)) ? 'sortable' : '';
      thClass += (this.isColumnSmallTh(column)) ? ' small' : '';
      thClass += (this.stickyHeaders) ? ' sticky top-0 ' : '';
      return thClass;
    },
    render(data) {
      this.unfilteredCount = data.rows.length;
      this.myTotal = data.rows.length;
      this.myData = data.rows;
      this.myColumns = data.columns;
      this.initialSort();
      this.initialized = true;
      this.$emit('data-fetched');

    },
    refreshTableData(rows) {
      this.render({rows: rows, columns: this.myColumns});
    },
    callApi(force) {
      let params = this.tableParams();
      if (this.paramsEqualLastFetch(params) && !force) {
        return;
      }

      let apiOptions = this.apiOptions ? this.apiOptions : {};
      apiOptions.params = (apiOptions.hasOwnProperty('params')) ? {...apiOptions.params, ...params} : params;

      this.lastFetch = params;
      this.$http.get(this.api, apiOptions)
          .then(({data}) => {
            this.unfilteredCount = data.numItems;
            this.myTotal = data.filteredCount;
            this.myData = data.items;
            this.initialized = true;
            this.$emit('data-fetched');
          })
          .catch(error => {
            this.apiHandleError(error);
          });
    },
    checkSort() {
      if (this.myColumns.length && this.firstTimeSort) {
        this.firstTimeSort = false;
        this.initialSort();
      } else if (this.myColumns.length) {
        if (this.currentSortColumn.field) {
          for (let i = 0; i < this.myColumns.length; i++) {
            if (this.myColumns[i].field === this.currentSortColumn.field) {
              this.currentSortColumn = this.myColumns[i];
              break
            }
          }
        }
      }
    },
    // Initial sorted column based on the default-sort prop.
    initialSort() {
      if (!this.myDefaultSort) {
        return;
      }
      let sortField = this.myDefaultSort;
      let sortDirection = this.myDefaultSortDirection;
      this.myColumns.forEach((column) => {
        if (column.field === sortField) {
          this.isAsc = sortDirection.toLowerCase() !== 'desc';
          this.sort(column, true)
        }
      });
    },
    isColumnSortable(column) {
      return column && column.sortable;
    },
    isColumnSmallTh(column) {
      return column && column.small;
    },
    onFilter(filter) {
      this.filter = filter;
      this.myCurrentPage = 1;
      if (this.api) {
        this.callApi();
      } else {
        this.myData = this.data.filter(item => this.filterMethod(item, this.search, filter));
        this.myTotal = this.myData.length;
      }
    },
    onPerPage(newPerPage) {
      this.myPerPage = parseInt(newPerPage);
      this.myCurrentPage = 1;
      this.tableCookieSave(this.id, this.currentSortColumn.field, this.isAsc, this.myPerPage);
      if (this.api) {
        this.callApi();
      }
    },
    onSearch(search) {
      this.search = search;
      this.myCurrentPage = 1;
      if (this.api) {
        this.callApi();
      } else {
        if (this.searchMethod) {
          this.myData = this.data.filter(item => this.searchMethod(item, search, this.filter));
        } else {
          let lowerSearch = search.toLowerCase();
          this.myData = this.data.filter((item) => {
            let found = false;
            for (let key in item) {
              if ((item[key] + '').toLowerCase().includes(lowerSearch)) {
                found = true;
                break;
              }
            }
            return found;
          });
        }
        this.myTotal = this.myData.length;
      }
    },
    onSetPage(page) {
      this.myCurrentPage = page;
      if (this.api) {
        this.callApi();
      }
    },
    onSort(value) {
      let sortColumn = {
        field: value[0], sortable: true
      };
      if (this.myColumns.length) {
        sortColumn = this.myColumns.find((col) => {
          return col.field === value[0];
        });
      }
      if (sortColumn) {
        this.isAsc = value[1] === 'asc';
        this.sort(sortColumn, true);
      }
    },
    paramsEqualLastFetch(params) {
      return params['filter'] === this.lastFetch['filter'] &&
          params['search'] === this.lastFetch['search'] &&
          params['order'] === this.lastFetch['order'] &&
          params['sort'] === this.lastFetch['sort'] &&
          params['perPage'] === this.lastFetch['perPage'] &&
          params['page'] === this.lastFetch['page'];
    },
    rowClicked(row, event) {
      if (this.rowClick) {
        this.$emit('rowClicked', row);
        event.preventDefault();
      }
    },
    sort(column, updatingData = false) {
      if (!this.isColumnSortable(column)) {
        return;
      }
      if (!updatingData) {
        this.isAsc = column === this.currentSortColumn
            ? !this.isAsc : (this.myDefaultSortDirection.toLowerCase() !== 'desc');
      }
      if (!this.api) {
        this.myData = this.sortBy(
            this.myData, column.field, column.customSort, this.isAsc
        );
      }
      this.currentSortColumn = column;
      if (this.api) {
        this.callApi();
      }
      this.tableCookieSave(this.id, this.currentSortColumn.field, this.isAsc, this.myPerPage);
    },
    /**
     * Sort an array by key without mutating original data.
     * Call the user sort function if it was passed.
     */
    sortBy(array, key, fn, isAsc) {
      let sorted = [];
      // Sorting without mutating original data
      if (fn && typeof fn === 'function') {
        sorted = [...array].sort((a, b) => fn(a, b, isAsc));
      } else {
        sorted = [...array].sort((a, b) => {
          // values from objects
          let newA = a[key];
          let newB = b[key];
          // sort boolean type
          if (typeof newA === 'boolean' && typeof newB === 'boolean') {
            return isAsc ? newA - newB : newB - newA;
          }
          if (!newA && newA !== 0) return 1;
          if (!newB && newB !== 0) return -1;
          if (newA === newB) return 0
          newA = (typeof newA === 'string')
              ? newA.toUpperCase()
              : newA;
          newB = (typeof newB === 'string')
              ? newB.toUpperCase()
              : newB;
          return isAsc
              ? newA > newB ? 1 : -1
              : newA > newB ? -1 : 1;
        });
      }
      return sorted;
    },
    tableCookieRead(id) {
      let value = this.$cookies.get('SCORECARD-TABLE');
      if (!id || !value) {
        return {};
      }
      value = value[id];
      return value ? value : {};
    },
    tableCookieSave(id, sort, isAsc, perPage) {
      if (!id) {
        return;
      }
      let value = this.$cookies.get('SCORECARD-TABLE');
      if (!value) {
        value = {};
      }
      value[id] = {
        sort: sort,
        order: isAsc ? 'asc' : 'desc',
        perPage: perPage
      };
      this.$cookies.set('SCORECARD-TABLE', value);
    },
    tableParams() {
      return {
        filter: this.filter,
        search: this.search,
        order: this.isAsc ? 'asc' : 'desc',
        sort: this.currentSortColumn['field'] ? this.currentSortColumn['field'] : this.myDefaultSort,
        perPage: this.myPerPage,
        page: this.myCurrentPage
      };
    }
  }
}
</script>